// AI Generated Component
import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

import { TranslateService } from '@client/app/services/translate/translate.service';
import { StateService, UrlService } from '@uirouter/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { AuditRepoService } from '@client/app/services/api/audit-repo/audit-repo.service';
import { AuditlogModulesService } from '@client/app/services/auditlog-modules/auditlog-modules.service';
import { ExporterService } from '@client/app/modules/export/services/exporter.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { activeFeatures } from '@monsido/ng2/core/constants/general.constant';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { AccessibilityService } from '@client/app/services/entities/accessibility/accessibility.service';
import { ToastService } from '@monsido/angular-shared-components';
import { AuditlogService } from './auditlog.component.service';
import { MonUIRouterTransitionService } from '@monsido/ng2/services/ui-router-transition-service/ui-router-transition-service';
import { UserService } from '@client/app/services/entities/user/user/user.service';
import { DEFAULTS } from '@monsido/ng2/core/constants/defaults.constant';
import moment from 'moment';
import {
    AdminAuditlogChangesComponent,
} from '@client/app/pages/admin/auditlog/changes/admin-auditlog-changes.component';
import {
    AdminAuditlogMoreInfoComponent,
} from '@client/app/pages/admin/auditlog/more/admin-auditlog-more-info.component';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';
import { Domain } from '@monsido/modules/models/api/domain';

@Component({
    selector: 'admin-auditlog',
    standalone: false,
    templateUrl: './admin-auditlog.component.html',
})
export class AdminAuditlogComponent extends BaseContainerComponentController implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    domains: any[] = [];
    users: any[] = [];
    modules: any[] = [];
    running = false;
    useDate = true;
    logs: any = [];
    date: any = {};
    dateFormat = '';
    options: any = {};
    domainIdModel: any = {};
    userIdModel: any = {};
    moduleModel: any = {};
    promise: any;

    private domain_id: any = 'all';
    private user_id: any = 'all';
    private module: any = 'all';

    constructor (
        private translateService: TranslateService,
        private auditRepoService: AuditRepoService,
        private stateService: StateService,
        private auditlogModulesService: AuditlogModulesService,
        private exporterService: ExporterService,
        private totalDomainsService: TotalDomainsService,
        private paramService: ParamService,
        private monModalService: MonModalService,
        private accessibilityService: AccessibilityService,
        private toastService: ToastService,
        private auditlogService: AuditlogService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
        private userService: UserService,
        private cdRef: ChangeDetectorRef,
        public override monEventsService: MonEventService,
        public override urlService: UrlService,
    ) {
        super({ table: true });
    }

    ngOnInit (): void {
        if (!activeFeatures.audit_log) {
            return this.monUIRouterTransitionService.onTransitionFinished(
                () => {
                    this.stateService.go('base.dashboard');
                },
            );
        }
        this.domains = [];
        this.users = [];
        this.modules = [];
        this.running = false;
        this.useDate = true;
        this.logs = [];
        this.date = {};
        this.dateFormat = DEFAULTS.DATE_FORMAT;
        this.options = { maxDate: new Date() };
        this.domainIdModel = {};
        this.userIdModel = {};
        this.moduleModel = {};

        // Assign our getAuditPage method to the getPage property
        this.getPage = this.getAuditPage.bind(this);

        this.loadLocations().then(async () => {
            this.setupModules();
            this.setupDomains();
            await this.setupUsers();
            this.cdRef.detectChanges();
        });
    }

    ngOnDestroy (): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getAuditPage (): void {
        this.paramService.setParams({
            page: this.page,
            page_size: this.pageSize,
            search: this.search,
            module: this.module,
            user_id: this.user_id,
            domain_id: this.domain_id,
            from: moment(this.date.startDate).format('YYYY-MM-DD'),
            to: moment(this.date.endDate).format('YYYY-MM-DD'),
        });

        this.getPages();
    }

    getSubHeaderText (): string {
        const total = this.logs.total || 0;
        const singular = this.translateService.getString('Found 1 audit log on your account');
        const plural = this.translateService.getString('Found [[count]] audit logs on your account', { count: total });
        return total === 1 ? singular : plural;
    }

    submit (event) {
        event.preventDefault();
        this.getAuditPage();
    }

    loadLocations (): Promise<void> {
        const search = this.urlService.search() || {};

        this.module = search.module || 'all';
        this.domain_id = search.domain_id ? parseInt(search.domain_id || '') || 'all' : 'all';
        this.user_id = search.user_id ? parseInt(search.user_id || '') || 'all' : 'all';

        const from = search.from;
        const to = search.to;
        if (to) {
            this.date.endDate = moment(to);
        } else {
            this.date.endDate = moment();
            this.date.endDate.set({ hours: 23, minutes: 59, seconds: 59, milliseconds: 999 });
        }
        if (from) {
            this.date.startDate = moment(from);
        } else {
            this.date.startDate = moment();
            this.date.startDate.subtract(30, 'days');
            this.date.startDate.startOf('day');
        }
        this.getAuditPage();
        return Promise.resolve();
    }

    updateDomainId (): void {
        this.domain_id = this.domainIdModel.value;
    }

    updateUserId (): void {
        this.user_id = this.userIdModel.value;
    }

    updateModule (): void {
        this.module = this.moduleModel.value;
    }

    private getParams (): any {
        const params: any = {};
        params.module = this.module === 'all' ? null : this.module;
        params.domain_id = this.domain_id === 'all' ? null : this.domain_id;
        params.user_id = this.user_id === 'all' ? null : this.user_id;
        params.page = this.page;
        params.page_size = this.pageSize;
        if (this.useDate) {
            params.from = moment(this.date.startDate).toISOString();
            params.to = moment(this.date.endDate).endOf('day').toISOString();
        }
        return params;
    }


    private getPages (): Promise<void> {
        this.promise = this.auditRepoService.getAuditLogs(this.getParams()).then((data: any) => {
            this.logs = data;
            this.updateDropdowns();
        });
        return this.promise;
    }

    private updateDropdowns (): void {
        for (const log of this.logs ?? []) {
            const dropdown = [
                {
                    label: this.translateService.getString('Show details'),
                    leftIcon: 'faInfoCircle',
                    action: () => this.showMore(log),
                },
                {
                    label: this.translateService.getString('Undo check review'),
                    leftIcon: 'faUndo',
                    action: () => this.undoAccessibilityCheck(log),
                },
            ];

            log.dropdown = dropdown;
        }
    }

    parseTargetURI (target: string): string {
        const temp = target.substring(0, target.indexOf(':'));

        if (temp === 'PageCorrect') {
            return temp;
        }
        return temp.replace(/([A-Z])/g, ' $1').trim();
    }

    private prepareDomains (domains: Domain[] | null): void {
        if (domains?.length) {
            const domainsArr: { name: string; value: number | string }[] = domains.map((domain) => {
                return { name: domain.title, value: domain.id };
            });
            domainsArr.unshift({
                value: 'all',
                name: this.translateService.getString('Domain') + ': ' + this.translateService.getString('All'),
            });
            this.domainIdModel = domainsArr.find(domain => domain.value === this.domain_id);
            this.domains = domainsArr;
            this.cdRef.markForCheck();
        }
    }

    private setupDomains (): void {
        this.totalDomainsService.domainsEssential.subscribe((domains) => this.prepareDomains(domains));
    }

    private async setupUsers (): Promise<void> {
        const users = await this.userService.tryGetUsersFromCache({ page: 1, page_size: 50 });
        const usersOptions = [{
            value: 'all',
            name: this.translateService.getString('User') + ':' + this.translateService.getString('All'),
        }];
        Object.values(users).forEach((user: any) => {
            usersOptions.push({ name: user.email, value: user.id });
        });
        this.users = usersOptions;
        this.userIdModel = this.users.find(user => user.value === this.user_id);
        this.cdRef.markForCheck();
    }

    private setupModules (): void {
        let modules = [
            {
                value: 'all',
                name: this.translateService.getString('Module') + ':' + this.translateService.getString('All'),
            },
        ];
        modules = modules.concat(this.auditlogModulesService.getList());
        this.modules = modules;
        this.moduleModel = this.modules.find(module => module.value === this.module);
        this.cdRef.markForCheck();
    }

    onDateChange (): void {
        this.page = 1;
        this.getAuditPage();
    }

    override onPageChange (page: number): void {
        this.page = page;
        this.getAuditPage();
    }

    override onPageSizeChange (pageSize: string): void {
        this.pageSize = Number(pageSize);
        this.page = 1;
        this.getAuditPage();
    }

    showChanges (log: any): void {
        const params = {
            body: AdminAuditlogChangesComponent,
            size: 'sm',
            data: {
                identifier: log.data.identifier,
                ngModel: log.data,
                created: ['created', 'create'].indexOf(log.action) !== -1,
            },
        };
        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    showMore (log: any): void {
        const params = {
            body: AdminAuditlogMoreInfoComponent,
            size: 'sm',
            data: {
                data: log.data,
                created: ['created', 'create'].indexOf(log.action) !== -1,
                action: log.action,
                target: log.apiTarget,
                sourceCodeId: log.apiTargetId,
                domainId: log.domain.id,
            },
        };

        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    isBlackListed (changes: any): boolean {
        const attrs = Object.getOwnPropertyNames(changes);
        return attrs.indexOf('updated_at') !== -1 && attrs.length === 1;
    }

    getModuleNameFromValue (value: string): string {
        return this.auditlogModulesService.getModuleNameFromValue(value);
    }

    async exportCsv (): Promise<any> {
        return async () => {
            try {
                const params = this.getParams();
                params.page_size = 100;
                delete params.page;
                this.running = true;
                const result = await this.exporterService.parseAuditLog(params, { total: this.logs.total });
                return result;
            } catch (error) {
                // NOSONAR - Intentionally swallow error, return undefined for export failure
                return undefined;
            } finally {
                this.running = false;
            }
        };
    }

    undoAccessibilityCheck (log: any): void {
        const sourceCodeId = log.apiTargetId;
        const msg = '<div class=\'text-line\'>' + this.translateService.getString('Add comment (Optional)');

        this.monModalService.prompt(msg, {}).then((comment: string) => {
            const params = {
                comment: comment,
                ignored_reviewed: false,
            };
            this.accessibilityService
                .undoAccessibilityCheck(log.domain.id, sourceCodeId, log.data.check_id, params)
                .then(() => {
                    this.toastService.success(this.translateService.getString('Snippet reset'));
                })
                .then(() => {
                    this.getPages();
                });
        });
    }

    isAnUndoLog (action: string): boolean {
        return this.auditlogService.isAnUndoLog(action);
    }

    canUndo (target: string): boolean {
        return this.auditlogService.canUndo(target);
    }

    trackByIndex (index: number): number {
        return index;
    }
}
